import { Button } from '@fluentui/react-components';
import type { MultipartPlan, MultipartStep } from '../types';
import { CheckIcon } from '../../shared/components/icons';
import styles from './MultipartPlanCard.module.css';

interface MultipartPlanCardProps {
  readonly plan: MultipartPlan;
  /** Disables the buttons while a step's turn is in flight. */
  readonly busy?: boolean;
  /** Run the step now — sends its `subTask` pinned to its `skillKey`. */
  readonly onRunStep: (step: MultipartStep) => void | Promise<void>;
  /** Drop this step and offer the next one. Never runs anything. */
  readonly onSkipStep: (step: MultipartStep) => void;
  /** Abandon the rest of the queue. */
  readonly onCancel: () => void;
}

type StepState = 'done' | 'skipped' | 'running' | 'current' | 'pending';

/** How each row of the step list renders on a card whose subject is `cursor`. */
function stepState(index: number, plan: MultipartPlan): StepState {
  const { cursor, outcome } = plan;
  // Everything before this card's step was settled by an earlier card. Which way
  // it went is that card's record to keep, so it just reads as behind us here.
  if (index < cursor) return 'done';
  if (index > cursor) return 'pending';
  if (outcome === 'skipped') return 'skipped';
  if (outcome !== undefined) return 'done';
  return plan.running === true ? 'running' : 'current';
}

/** The line a settled card is left showing, in place of its question. */
function stamp(plan: MultipartPlan): string | null {
  const label = plan.steps[plan.cursor]?.label ?? '';
  switch (plan.outcome) {
    case 'cancelled':
      return 'Stopped — ask again for anything still outstanding.';
    case 'skipped':
      return `Skipped “${label}”.`;
    case 'ran':
      // Only the LAST step's card can claim the plan is finished.
      return plan.cursor >= plan.steps.length - 1 ? 'All steps run.' : `Ran “${label}”.`;
    default:
      return null;
  }
}

/**
 * Sequential-multipart plan card.
 *
 * A request that asks for several things at once ("fix the numbering and the
 * alignment issues") is not run as one turn. The backend planner splits it into
 * ordered steps and this card asks before ONE of them — the user confirms, the
 * step runs through its EXISTING single-skill flow, and when it finishes the
 * next step's question arrives as a NEW card below that step's answer.
 *
 * **One card per step, each frozen where it was asked.** A single card advancing
 * in place would have to move down the transcript on every step, which reads as
 * the UI jumping around; and parked at the top it would sit above every answer,
 * forcing a scroll back up to authorise each step. So each card stays put and
 * keeps its own record — what it offered and how it was answered.
 *
 * **Only the opening card lays out the plan.** That card IS the proposal, and
 * the list is what the user is agreeing to. Follow-ups (`compact`) ask about
 * their own step alone: repeating the list there re-states a question already
 * answered and reads as though AiVA is asking the original combined request
 * again.
 *
 * **Nothing here is implicit.** Each step needs its own click; there is no
 * "run the rest" affordance, because the point of sequencing is that the user
 * sees each result before authorising the next piece of work on their document.
 * Skip and Cancel are offered throughout — a queue the user cannot get out of
 * is worse than one turn that did too much.
 *
 * Once exhausted (or cancelled) the card stays visible and stamped rather than
 * disappearing: the transcript should still show what was offered and how far
 * it got.
 */
export function MultipartPlanCard({
  plan,
  busy = false,
  onRunStep,
  onSkipStep,
  onCancel,
}: Readonly<MultipartPlanCardProps>) {
  const { steps, cursor } = plan;
  const current = steps[cursor];
  const settledText = current === undefined ? 'All steps run.' : stamp(plan);
  // Settled: this card has been answered and is now a record, not a question.
  const settled = settledText !== null;
  // Running: this card's step is executing. It is no longer a question either,
  // and the next one is not asked until this lands.
  const running = !settled && plan.running === true && current !== undefined;
  const remaining = steps.length - cursor;

  /**
   * What sits under the step list: a stamp, a progress line, or the question.
   *
   * A function with early returns rather than a nested ternary (sonar
   * typescript:S3358) — there are four outcomes here and the chained form put
   * three conditions on one line.
   */
  const renderFooter = () => {
    if (settled) return <div className={styles.stamp}>{settledText}</div>;
    // Nothing left to ask about: the queue is exhausted.
    if (current === undefined) return null;
    if (running) {
      // No question and no buttons while the step runs. The next step is only
      // offered once this one has landed, so the card must not read as though
      // it is still waiting for an answer it already has.
      return (
        <div className={styles.stamp} aria-live="polite">
          {`Working on “${current.label}”…`}
        </div>
      );
    }
    return (
      <>
        <div className={styles.prompt}>
          Shall I {current.label.charAt(0).toLowerCase()}
          {current.label.slice(1)} now?
        </div>
        <div className={styles.actions}>
          <Button appearance="primary" size="small" disabled={busy} onClick={() => onRunStep(current)}>
            Yes, do this
          </Button>
          {/* Skipping is only meaningful while something else remains — on the
              last step it would be indistinguishable from Cancel, and two
              buttons that do the same thing is a worse choice than one. */}
          {remaining > 1 && (
            <Button appearance="secondary" size="small" disabled={busy} onClick={() => onSkipStep(current)}>
              Skip
            </Button>
          )}
          <Button appearance="subtle" size="small" disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </>
    );
  };

  return (
    <fieldset className={`${styles.card} ${settled ? styles.finished : ''}`} aria-label="Multi-step request">
      <div className={styles.title}>
        {current === undefined ? 'Multi-step request' : `Step ${cursor + 1} of ${steps.length}`}
      </div>

      {/* The opening card lays out the whole plan, because that is what the
          user is agreeing to. Follow-ups ask about their own step alone —
          repeating the list there re-states a question already answered. */}
      {plan.compact !== true && (
      <ol className={styles.steps}>
        {steps.map((step, index) => {
          const state = stepState(index, plan);
          return (
            <li
              // Steps are backend-generated and have no id; the skill key is
              // unique within a plan (the planner rejects a plan that repeats
              // one), so it is a stable key here.
              key={step.skillKey}
              className={`${styles.step} ${styles[state]}`}
              aria-current={state === 'current' ? 'step' : undefined}
            >
              <span className={styles.stepMark} aria-hidden="true">
                {state === 'done' ? <CheckIcon /> : index + 1}
              </span>
              <span className={styles.stepLabel}>{step.label}</span>
              {/* Spelled out for screen readers because the tick alone is
                  ambiguous — and deliberately says "already run", not
                  "done": see the note in MultipartPlanCard.module.css. */}
              {state === 'done' && <span className={styles.srOnly}>already run</span>}
              {state === 'skipped' && <span className={styles.srOnly}>skipped</span>}
              {state === 'running' && <span className={styles.srOnly}>running now</span>}
            </li>
          );
        })}
      </ol>
      )}

      {renderFooter()}

    </fieldset>
  );
}
